<script setup lang="ts">
import { storeToRefs } from 'pinia'

import BandSignupInit from '~/components/band/signup/init.vue'
import IndexMainTitle from '~/components/index/mainTitle.vue'
import IndexVideoPresentation from '~/components/index/videoPresentation.vue'
import { BtnOrange } from '~/components/ui/Buttons'
import LogoIcon from '~/components/ui/LogoIcon/LogoIcon.vue'
import VText from '~/components/ui/VText.vue'

import { useGetMediaUrl } from '~/composables/useGetMediaUrl'

import { useMiscResizeStore } from '~/stores/miscResize'

import { trackEvent } from '~/helpers/LegacyTrackEvent'

import { createMetaObjectGraphObject } from '~/utils/create-meta-open-graph-object'

import { Breakpoints } from '~/enums/breakpoints'

import type { PropConfigType } from '~/components/ui/VText.vue'

const route = useRoute()
const { t } = useI18n()
const { getMediaUrl } = useGetMediaUrl()

const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())

const displayAdditionalBlock = ref(true)

definePageMeta({
  middleware: function () {
    const { $localePath, $pinia } = useNuxtApp()
    const userStore = useUserStore($pinia)
    if (userStore.IS_LOGGED_IN && !userStore.IS_INCOMPLETE_USER)
      return navigateTo($localePath('/'))
    if (userStore.IS_INCOMPLETE_USER)
      return navigateTo($localePath('/band/signup/'))
  },
})

const title = t('pages.signup.meta.title')
const description = t('pages.signup.meta.description')

useHead({
  title,
  meta: createMetaObjectGraphObject({
    title,
    description,
  }),
})

onMounted(() => {
  if (route.query.utm_campaign && route.query.utm_campaign === 'cdbaby') {
    // we currently use this local storage value to track whether signups come from cd baby
    window.localStorage.setItem('comesFromCustomSignup', 'true')
  }
})

const titleTextConfig = computed<PropConfigType>(() =>
  SCREEN_WIDTH.value < Breakpoints.xs375 ? 'sans/32/bold' : 'sans/40/bold',
)

const logoTextConfig = computed<PropConfigType>(() => {
  return SCREEN_WIDTH.value < Breakpoints.md ? 'sans/16/bold' : 'sans/32/bold'
})

function handleButtonBtnClick() {
  const el = window.document.getElementById('signupScrollAnchor')
  const heightTillTopOfContainer = 80
  if (!el) return

  window.scrollTo({
    left: 0,
    behavior: 'smooth',
    top: el.offsetTop - (heightTillTopOfContainer + 24),
  })
  trackEvent(
    {
      category: 'Custom Signup',
      action: 'Click signup with email',
    },
    route,
  )
}
</script>

<template>
  <div>
    <div
      class="tw-relative tw-w-full tw-overflow-hidden tw-bg-orange-500 tw-p-6 sm:tw-pb-[104px] sm:tw-pt-10 md:tw-pb-[128px] md:tw-pt-12"
    >
      <div class="groover-page-wrapper tw-relative">
        <LogoIcon class="bg-logo" size-override="480px" />
        <div
          class="tw-grid tw-grid-cols-1 tw-justify-center tw-gap-2 tw-text-center"
        >
          <div class="tw-flex tw-items-center tw-justify-center tw-gap-1">
            <img
              :src="getMediaUrl('GrooverLogoWithText-White.svg')"
              width="136px"
              alt="Groover logo"
            />
          </div>
          <VText
            :cfg="titleTextConfig"
            color="white"
            class="title-text tw-mx-auto tw-w-full tw-leading-10 375:tw-leading-[50px]"
            >{{ $t('pages.signup.titles.main') }}</VText
          >
        </div>
      </div>
    </div>
    <div id="signupScrollAnchor" class="contentWrapper">
      <div class="signupWrapper tw-mb-6 sm:tw-mb-12 md:tw-mb-20">
        <BandSignupInit
          no-float
          is-stand-alone
          method-select-has-compact-display
          @signup-method-selected="displayAdditionalBlock = !$event"
        />
        <Transition mode="out-in" name="fade">
          <div v-if="displayAdditionalBlock" class="tw-pb-6 sm:tw-p-6">
            <div
              class="groover-page-wrapper tw-space-y-6 tw-overflow-hidden tw-rounded-xs tw-border tw-border-solid tw-border-gray-300 tw-p-5"
            >
              <VText
                cfg="sans/24/bold"
                class="tw-inline-block tw-w-full tw-text-center"
                >{{ $t('pages.signup.titles.deservedToBeheard') }}</VText
              >
              <div class="tw-grid tw-grid-cols-1 tw-gap-4">
                <div class="check-text-holder">
                  <i class="fa-solid fa-check" />
                  <VText cfg="sans/16/regular">{{
                    $t('pages.signup.text.directAndFastAccess')
                  }}</VText>
                </div>
                <div class="check-text-holder">
                  <i class="fa-solid fa-check" />
                  <VText cfg="sans/16/regular">{{
                    $t('pages.signup.text.fastAccess')
                  }}</VText>
                </div>
                <div class="check-text-holder">
                  <i class="fa-solid fa-check" />
                  <VText cfg="sans/16/regular">{{
                    $t('pages.signup.text.accessToAll')
                  }}</VText>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
      <IndexMainTitle class="marginAdjust tw-mb-6 md:tw-mb-10">
        <i18n-t keypath="index.title.howItWorks">
          <template #lineBreak>
            <br class="600:tw-hidden" />
          </template>
        </i18n-t>
      </IndexMainTitle>
      <div
        class="videoPresentationContainer offsettedBackground tw-pb-6 md:tw-pb-10"
      >
        <IndexVideoPresentation />
      </div>
      <div
        class="tw-flex tw-items-center tw-justify-center tw-bg-white tw-py-6 md:tw-py-10"
      >
        <BtnOrange @click="handleButtonBtnClick">{{
          $t('band.signup.init.selectMethod.button.classic')
        }}</BtnOrange>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.bg-logo {
  @apply tw-absolute tw-text-white;

  --tw-text-opacity: 0.1;
  --local-grid-offset: var(--grid-2);
  left: calc(var(--local-grid-offset) - var(--grid-gap));
  top: 24px;
  font-size: 400px;

  @screen md {
    --local-grid-offset: var(--grid-4);
    font-size: 480px;
  }

  @screen lg {
    --local-grid-offset: var(--grid-8);
  }
}

.offsettedBackground {
  @apply tw-relative tw-w-full;

  &::before {
    @apply tw-absolute tw-left-0 -tw-z-10 tw-w-full tw-bg-orange-500 tw-bg-opacity-10 tw-content-[''];

    --offset: 124px;
    top: var(--offset);
    height: calc(100% - var(--offset));

    @screen lg {
      --offset: 64px;
    }
  }

  &.videoPresentationContainer::before {
    --offset: 336px;

    @screen 375 {
      --offset: 353px;
    }
    @screen 600 {
      --offset: 328px;
    }
    @screen md {
      --offset: 404px;
    }
    @screen lg {
      --offset: 61px;
    }
  }
}

.contentWrapper {
  --offset: 0px;

  @screen sm {
    --offset: -64px;
  }

  @screen md {
    --offset: -80px;
  }

  .marginAdjust {
    margin-top: var(--offset);
  }

  .signupWrapper {
    @apply tw-bg-white;
    transform: translateY(var(--offset));

    @screen sm {
      @apply tw-mx-auto tw-bg-white tw-shadow;

      width: var(--page-width);
    }

    @screen md {
      width: var(--grid-6);
    }

    @screen lg {
      width: var(--grid-6);
    }
  }
}
.check-text-holder {
  @apply tw-flex tw-items-start tw-justify-start tw-gap-2;

  > i {
    @apply tw-flex-shrink-0 tw-text-base tw-text-orange-500;
  }

  > span {
    @apply -tw-mt-1 tw-block;
  }
}

.signupWrapper :deep(.wrapper.hasTitle > .title) {
  @apply tw-border-none tw-shadow-none;
}

.signupWrapper :deep(#signupScrollAnchor) {
  @apply tw-pt-0;
}

.title-text {
  @screen md {
    width: var(--grid-6);
  }
}
</style>
